import { useState, useRef, useEffect } from 'react';
import { X, Image as ImageIcon } from 'lucide-react';
import { discussionService } from '@services/discussionService';
import { useAuthStore } from '@store/authStore';
import toast from 'react-hot-toast';

interface CreateDiscussionInlineProps {
    onSuccess: (discussionId: string) => void;
    initialType?: string;
    autoExpand?: boolean;
    onDiscussionCreated?: () => void;
}

// Content length limits (must match backend)
const MIN_DISCUSSION_LENGTH = 20;
const MAX_CONTENT_LENGTH = 5000;

const DISCUSSION_TYPES = [
    {
        value: 'general',
        label: 'General',
        activeClass: 'bg-purple-500/20 text-purple-300 border-purple-500/50'
    },
    {
        value: 'announcement',
        label: 'Announcement',
        activeClass: 'bg-orange-500/20 text-orange-300 border-orange-500/50',
        adminOnly: true
    },
    {
        value: 'bug',
        label: 'Bug',
        activeClass: 'bg-red-500/20 text-red-300 border-red-500/50'
    },
    {
        value: 'feature',
        label: 'Feature',
        activeClass: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50'
    },
    {
        value: 'question',
        label: 'Question',
        activeClass: 'bg-blue-500/20 text-blue-300 border-blue-500/50'
    },
    {
        value: 'feedback',
        label: 'Feedback',
        activeClass: 'bg-green-500/20 text-green-300 border-green-500/50'
    },
    {
        value: 'astrology',
        label: 'Astrology',
        activeClass: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/50'
    },
];

const CreateDiscussionInline = ({ onSuccess, initialType, autoExpand, onDiscussionCreated }: CreateDiscussionInlineProps) => {
    const { user, updateBalance } = useAuthStore();
    const [isExpanded, setIsExpanded] = useState(autoExpand || false);
    const [selectedTypes, setSelectedTypes] = useState<string[]>(initialType ? [initialType] : ['general']);
    const [content, setContent] = useState('');
    const [image, setImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Auto-expand and scroll to input when autoExpand is true
    useEffect(() => {
        if (autoExpand) {
            setIsExpanded(true);
            // Scroll to top after a small delay to ensure component is rendered
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100);
        }
    }, [autoExpand]);

    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
            toast.error('Only JPG and PNG images are allowed');
            return;
        }

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size must be less than 5MB');
            return;
        }

        setImage(file);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveImage = () => {
        setImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const toggleType = (typeValue: string) => {
        setSelectedTypes(prev => {
            if (prev.includes(typeValue)) {
                // Don't allow removing if it's the last type
                if (prev.length === 1) {
                    toast.error('At least one type must be selected');
                    return prev;
                }
                return prev.filter(t => t !== typeValue);
            } else {
                return [...prev, typeValue];
            }
        });
    };

    const handleSubmit = async () => {
        if (!content.trim()) {
            toast.error('Please write something');
            return;
        }

        if (content.trim().length < MIN_DISCUSSION_LENGTH) {
            toast.error(`Discussion must be at least ${MIN_DISCUSSION_LENGTH} characters`);
            return;
        }

        setLoading(true);
        try {
            const response = await discussionService.createDiscussion({
                types: selectedTypes,
                content: content.trim(),
                image: image || undefined,
            });

            // Show success message with reward info if applicable
            if (response.is_first_discussion) {
                // Sync wallet balance from the backend's authoritative value
                if (typeof response.new_balance === 'number') {
                    updateBalance(response.new_balance);
                }

                toast.success(response.message, {
                    duration: 5000,
                    icon: '🎁',
                    style: {
                        background: '#7c3aed',
                        color: '#fff',
                        fontWeight: 'bold',
                    },
                });
                // Notify parent that discussion was created
                onDiscussionCreated?.();
            } else {
                // Backend message may include a hint (e.g. verify phone to earn reward)
                toast.success(response.message || 'Discussion posted!', { duration: 5000 });
            }

            onSuccess(response.discussion_id);

            // Reset form
            setSelectedTypes(['general']);
            setContent('');
            setImage(null);
            setImagePreview(null);
            setIsExpanded(false);
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to post discussion');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setIsExpanded(false);
        setSelectedTypes(['general']);
        setContent('');
        setImage(null);
        setImagePreview(null);
    };

    return (
        <div className="bg-white/[0.08] border border-white/20 rounded-3xl p-4 md:p-6 mb-6 hover:border-purple-500/30 transition-all">
            <div className={`flex gap-3 md:gap-4 ${isExpanded ? 'items-start' : 'items-center'}`}>
                {/* User Avatar - Aligned with textarea */}
                <div className={isExpanded ? 'pt-12' : ''}>
                    {((user?.profile_picture && user.profile_picture.trim() !== '') || (user?.picture && user.picture.trim() !== '')) ? (
                        <img
                            src={(user?.profile_picture && user.profile_picture.trim() !== '') ? user.profile_picture : user?.picture || ''}
                            alt={user?.username || 'User'}
                            className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-white/20 flex-shrink-0 object-cover"
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div
                        className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold flex-shrink-0"
                        style={{ display: ((user?.profile_picture && user.profile_picture.trim() !== '') || (user?.picture && user.picture.trim() !== '')) ? 'none' : 'flex' }}
                    >
                        {(user?.full_name?.[0] || user?.username?.[0])?.toUpperCase() || 'U'}
                    </div>
                </div>

                {/* Input Area */}
                <div className="flex-1">
                    {!isExpanded ? (
                        // Collapsed state
                        <div
                            className="cursor-pointer"
                            onClick={() => setIsExpanded(true)}
                        >
                            <div className="text-purple-300/50 text-base md:text-lg font-sans py-3 hover:text-purple-300/70 transition-colors">
                                What's on your mind?
                            </div>
                        </div>
                    ) : (
                        // Expanded state
                        <div className="space-y-4">
                            {/* Type Badges - Multi-select */}
                            <div className="flex flex-wrap gap-2 pb-3 border-b border-white/10">
                                <span className="text-xs text-purple-300/60 font-sans self-center mr-1">
                                    Select tags:
                                </span>
                                {DISCUSSION_TYPES.filter(t => !t.adminOnly || user?.is_admin).map((t) => (
                                    <button
                                        key={t.value}
                                        onClick={() => toggleType(t.value)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase transition-all border-2 ${selectedTypes.includes(t.value)
                                            ? t.activeClass
                                            : 'bg-white/5 text-white/40 border-white/10 hover:border-white/20 hover:text-white/60'
                                            }`}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>

                            {/* Content */}
                            <div>
                                <textarea
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    placeholder="Share your thoughts..."
                                    rows={4}
                                    autoFocus
                                    maxLength={MAX_CONTENT_LENGTH}
                                    className="w-full bg-transparent border-none text-white text-base placeholder:text-white/30 focus:outline-none font-sans resize-none"
                                />
                                {/* Character Counter - Twitter style */}
                                <div className="flex items-center justify-end gap-2 mt-1">
                                    {content.length < MIN_DISCUSSION_LENGTH && (
                                        <span className="text-xs text-yellow-400/80 font-sans">
                                            Min {MIN_DISCUSSION_LENGTH} chars
                                        </span>
                                    )}
                                    <span className={`text-xs font-sans ${
                                        content.length < MIN_DISCUSSION_LENGTH
                                            ? 'text-yellow-400'
                                            : content.length > MAX_CONTENT_LENGTH - 100
                                                ? 'text-red-400'
                                                : content.length > MAX_CONTENT_LENGTH - 500
                                                    ? 'text-yellow-400'
                                                    : 'text-purple-300/60'
                                    }`}>
                                        {content.length}/{MAX_CONTENT_LENGTH}
                                    </span>
                                </div>
                            </div>

                            {/* Image Preview */}
                            {imagePreview && (
                                <div className="relative w-fit mx-auto">
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        className="max-h-64 md:max-h-96 w-auto rounded-xl border border-white/20 block"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleRemoveImage}
                                        className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg z-10"
                                    >
                                        <X size={14} className="text-white" />
                                    </button>
                                </div>
                            )}

                            {/* Divider */}
                            <div className="border-t border-white/10" />

                            {/* Actions */}
                            <div className="flex items-center justify-between">
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="p-2 rounded-lg bg-white/5 hover:bg-purple-500/20 text-purple-400 transition-all"
                                    title="Add image"
                                >
                                    <ImageIcon size={20} />
                                </button>

                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={handleCancel}
                                        disabled={loading}
                                        className="px-4 py-2 text-white/60 hover:text-white font-bold rounded-full transition-all text-sm md:text-base font-sans disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={loading || !content.trim() || content.trim().length < MIN_DISCUSSION_LENGTH}
                                        className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold py-2 px-6 rounded-full transition-all shadow-lg text-sm md:text-base font-sans disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                                    >
                                        {loading ? 'Posting...' : 'Post'}
                                    </button>
                                </div>
                            </div>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/jpg,image/png"
                                onChange={handleImageSelect}
                                className="hidden"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateDiscussionInline;
